import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Container,
  Button,
  CircularProgress,
  Typography,
  Dialog,
  DialogContent,
} from "@mui/material";
import Cookies from "js-cookie";
import PostExamUploadHeader from "./PostExamUploadHeader";
import QuestionCard from "./QuestionCard";
import MessageScreen from "./MessageScreen";
import {
  fetchPostExamQuestions,
  uploadAnswerFile,
  deleteAnswerFile,
  submitUploadedAnswers,
} from "../../services/postExamUploadService";
import { ensureFullscreen } from "../../utils/fullScreenHelper";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SCREEN = {
  LOADING: "loading",
  VALID: "valid",
  EXPIRED: "expired",
  ALREADY_SUBMITTED: "already_submitted",
  NOT_FOUND: "not_found",
  ERROR: "error",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const parseRemainingSeconds = (timeStr) => {
  if (!timeStr) return 0;
  const parts = timeStr.split(":").map(Number);
  if (parts.some(isNaN)) return 0;
  if (parts.length === 3) {
    const [h, m, s] = parts;
    return h * 3600 + m * 60 + s;
  }
  if (parts.length === 2) {
    const [h, m] = parts;
    return h * 3600 + m * 60;
  }
  return 0;
};

const calcRemainingFromEndTime = (endTimeIso) => {
  if (!endTimeIso) return 0;
  const diff = Math.floor(
    (new Date(endTimeIso).getTime() - Date.now()) / 1000
  );
  return Math.max(diff, 0);
};

const makeExistingFileProxy = (srNo, path) => {
  const pathWithoutQuery = path?.split("?")[0] ?? "";
  const lastSegment = pathWithoutQuery.split("/").pop() ?? "";
  const prefixPattern = new RegExp(`^srNo-${srNo}-\\d+-`);
  const name =
    decodeURIComponent(lastSegment.replace(prefixPattern, "")) ||
    `Q${srNo}_answer`;
  return { name, size: 0, _isExisting: true, _path: path };
};

const buildInitialUploadStates = (questions) => {
  const initial = {};
  (questions ?? []).forEach((q) => {
    if (q.descriptiveUploadAnsUrl) {
      initial[q.srNo] = {
        file: makeExistingFileProxy(q.srNo, q.descriptiveUploadAnsUrl),
        status: "success",
        fileUrl: q.descriptiveUploadAnsUrl,
        error: null,
      };
    }
  });
  return initial;
};

const mapQuestions = (questions) =>
  (questions ?? []).map((q) => ({
    id: q.srNo,
    questionNo: q.srNo,
    text: q.question,
    sectionName: q.sectionName ?? null,
  }));


function ConfirmSubmitModal({ open, onConfirm, onCancel, isSubmitting }) {
  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          p: { xs: 2.5, sm: 3.5 },
          textAlign: "center",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Typography
          sx={{
            fontSize: { xs: "18px", sm: "20px" },
            fontWeight: "bold",
            color: "#1e1e1e",
            mb: 3,
            lineHeight: 1.4,
          }}
        >
          Are you sure you want to submit?
        </Typography>

        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          {/* YES */}
          <Button
            onClick={onConfirm}
            disabled={isSubmitting}
            sx={{
              border: "1.5px solid #3F41D1",
              color: "#3F41D1",
              borderRadius: "28px",
              px: { xs: 3, sm: 5 },
              py: 1,
              fontSize: "13px",
              fontWeight: "bold",
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              minWidth: "100px",
              backgroundColor: "transparent",
              "&:hover": { backgroundColor: "rgba(63,65,209,0.06)" },
              "&.Mui-disabled": { opacity: 0.5 },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={18} sx={{ color: "#3F41D1" }} />
            ) : (
              "YES"
            )}
          </Button>

          {/* NO */}
          <Button
            onClick={onCancel}
            disabled={isSubmitting}
            sx={{
              backgroundColor: "#E598AC",
              color: "#1e1e1e",
              borderRadius: "28px",
              px: { xs: 3, sm: 5 },
              py: 1,
              fontSize: "13px",
              fontWeight: "bold",
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              minWidth: "100px",
              "&:hover": { backgroundColor: "#d4849a" },
              "&.Mui-disabled": { opacity: 0.5 },
            }}
          >
            NO
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// SuccessModal
// ---------------------------------------------------------------------------

function SuccessModal({ open }) {
  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          p: { xs: 3, sm: 4 },
          textAlign: "center",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            width: { xs: 64, sm: 72 },
            height: { xs: 64, sm: 72 },
            borderRadius: "50%",
            border: "3px solid #2e7d32",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2.5,
          }}
        >
          <svg
            width="34"
            height="34"
            viewBox="0 0 34 34"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7 17L13.5 23.5L27 10"
              stroke="#2e7d32"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>

        <Typography
          sx={{
            fontSize: { xs: "16px", sm: "18px" },
            fontWeight: 600,
            color: "#1e1e1e",
          }}
        >
          Successfully submitted
        </Typography>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// PostExamUpload
// ---------------------------------------------------------------------------

export default function PostExamUpload() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { test_engine_project: projectFromUrl } = useParams();

  const token =
    searchParams.get("token") || [...searchParams.keys()][0] || null;

  // ── Screen state ──────────────────────────────────────────────────────────
  const [screen, setScreen] = useState(SCREEN.LOADING);
  const [errorMessage, setErrorMessage] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [uploadStates, setUploadStates] = useState({});

  const [uploadDuration, setUploadDuration] = useState(0);

  const [isExpired, setIsExpired] = useState(false);

  // ── Submission state ──────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // ── Modal state ───────────────────────────────────────────────────────────
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // ── Refresh trigger ───────────────────────────────────────────────────────
  const [refreshTick, setRefreshTick] = useState(0);
  const refreshData = useCallback(() => setRefreshTick((t) => t + 1), []);

  // ── Core data loader ──────────────────────────────────────────────────────
  const loadData = useCallback(
    async (isInitial = false) => {
      if (!token) {
        if (isInitial) setScreen(SCREEN.NOT_FOUND);
        return;
      }

      try {
        const data = await fetchPostExamQuestions(token);

        if (isInitial) {
          if (data?.postExamSubmittedAt) {
            setScreen(SCREEN.ALREADY_SUBMITTED);
            return;
          }

          let remaining = 0;

          if (data?.endTime) {
            remaining = calcRemainingFromEndTime(data.endTime);
          } else if (data?.remainingTime) {
            remaining = parseRemainingSeconds(data.remainingTime);
          }

          if (remaining <= 0) {
     
            setIsExpired(true);
            remaining = 0; 
          }


          setSessionInfo({
            candidateName: data.candidateName ?? "",
            rollNo: data.rollNo ?? data.userName ?? "",
            courseName: data.courseName ?? "",
            endTime: data.endTime ?? null,
          });

          setUploadDuration(remaining);
          setScreen(SCREEN.VALID);
        }

        setQuestions(mapQuestions(data.questions));
        setUploadStates(buildInitialUploadStates(data.questions));
      } catch (err) {
        if (isInitial) {
          const code = err?.code;
          const msg = err?.message || "Something went wrong";

          switch (code) {
            case "ANSWER_UPLOAD_TIME_OVER":
              setScreen(SCREEN.EXPIRED);
              break;
            case "EXAM_ENDED":
              setScreen(SCREEN.ALREADY_SUBMITTED);
              break;
            case "INVALID_TOKEN":
              setScreen(SCREEN.NOT_FOUND);
              break;
            default:
              setErrorMessage(msg);
              setScreen(SCREEN.ERROR);
          }
        }
    
      }
    },
    [token]
  );

  useEffect(() => {
    ensureFullscreen();
    loadData(true);
  }, [loadData]);

  useEffect(() => {
    if (refreshTick === 0) return;
    loadData(false);
  }, [refreshTick, loadData]);

  // ── File selection & upload ───────────────────────────────────────────────
  const handleFileSelect = useCallback(
    async (srNo, file, validationError) => {
      if (validationError) {
        setUploadStates((prev) => ({
          ...prev,
          [srNo]: { file: null, status: null, fileUrl: null, error: validationError },
        }));
        return;
      }

      setUploadStates((prev) => ({
        ...prev,
        [srNo]: { file, status: "uploading", fileUrl: null, error: null },
      }));

      try {
        const uploadResult = await uploadAnswerFile(file, srNo, token);
        const fileUrl =
          uploadResult?.descriptiveUploadAnsUrl ??
          uploadResult?.filePath ??
          null;

        setUploadStates((prev) => ({
          ...prev,
          [srNo]: {
            file,
            status: "success",
            fileUrl,
            error: null,
          },
        }));
      } catch (err) {
        const code = err?.code ?? err?.response?.data?.code;
        const msg =
          err?.message ??
          err?.response?.data?.message ??
          "Upload failed. Please retry.";

        if (code === "ANSWER_UPLOAD_TIME_OVER") {
          setIsExpired(true);
        }

        setUploadStates((prev) => ({
          ...prev,
          [srNo]: { file, status: "failed", fileUrl: null, error: msg },
        }));
      }
    },
    [token, refreshData]
  );

  // ── Delete uploaded file ──────────────────────────────────────────────────
  const handleDelete = useCallback(
    async (srNo) => {
      setUploadStates((prev) => {
        const next = { ...prev };
        delete next[srNo];
        return next;
      });

      try {
        await deleteAnswerFile(srNo, token);
      } catch (err) {
        const code = err?.code ?? err?.response?.data?.code;
        if (code === "ANSWER_UPLOAD_TIME_OVER") {
          setIsExpired(true);
        }
        
      }

      refreshData();
    },
    [token, refreshData]
  );

  // ── View file ─────────────────────────────────────────────────────────────
  const handleView = useCallback((fileUrl) => {
    if (!fileUrl) return;
    const url = fileUrl.startsWith("http")
      ? fileUrl
      : `${window.location.origin}${fileUrl}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  // ── Retry failed upload ───────────────────────────────────────────────────
  
  const handleRetry = useCallback((srNo) => {
    setUploadStates((prev) => {
      const next = { ...prev };
      delete next[srNo];
      return next;
    });
  }, []);

  // ── Submit flow ───────────────────────────────────────────────────────────

  const handleSubmitClick = () => {
    if (isSubmitting) return;
    setSubmitError(null);
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    try {
      await submitUploadedAnswers(token);
      setShowConfirmModal(false);
      setShowSuccessModal(true);
      setIsSubmitted(true);
    } catch (err) {
      const code = err?.code ?? err?.response?.data?.code;
      const msg = err?.message ?? err?.response?.data?.message ?? "Submission failed.";

      if (code === "EXAM_ENDED") {
        setScreen(SCREEN.ALREADY_SUBMITTED);
        return;
      }

      setSubmitError(msg);
      setIsSubmitting(false);
      setShowConfirmModal(false);
    }
  };

  const handleCancelSubmit = () => {
    if (isSubmitting) return;
    setShowConfirmModal(false);
  };

  useEffect(() => {
    if (!isSubmitted) return;
    const timer = setTimeout(() => {
      const project = projectFromUrl || Cookies.get("test_engine_project");
      navigate(project ? `/${project}/login` : "/login");
    }, 2000);
    return () => clearTimeout(timer);
  }, [isSubmitted, navigate, projectFromUrl]);

  // ── Derived flags ─────────────────────────────────────────────────────────
  const isAnyUploading = Object.values(uploadStates).some(
    (s) => s.status === "uploading"
  );

  const uploadLocked = isExpired || isAnyUploading || isSubmitting;

  const submitDisabled = isSubmitting || isAnyUploading;

  // ── Edge-case screens ─────────────────────────────────────────────────────

  if (screen === SCREEN.LOADING) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f0f0fb",
        }}
      >
        <CircularProgress sx={{ color: "#3F41D1" }} />
      </Box>
    );
  }

  if (screen === SCREEN.EXPIRED) {
    return (
      <MessageScreen
        title="Upload Link Expired"
        description="Your upload window has expired."
        info="The allowed upload duration has ended. Please contact your exam coordinator."
      />
    );
  }

  if (screen === SCREEN.ALREADY_SUBMITTED) {
    return (
      <MessageScreen
        title="Already Submitted"
        description="You have already submitted your answers."
      />
    );
  }

  if (screen === SCREEN.NOT_FOUND) {
    return (
      <MessageScreen
        title="Page Not Found"
        description="The link is invalid or no longer exists."
        showHomeButton={false}
      />
    );
  }

  if (screen === SCREEN.ERROR) {
    return (
      <MessageScreen
        title="Something Went Wrong"
        description={
          errorMessage || "Unable to load the upload session. Please try again."
        }
      />
    );
  }

  // ── Main UI ───────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        height: "100dvh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#FFFFFF",
      }}
    >
      {/* ── Fixed Header ─────────────────────────────────────────────────── */}
      <Box sx={{ flexShrink: 0 }}>
        <PostExamUploadHeader
          candidateName={sessionInfo.candidateName}
          rollNo={sessionInfo.rollNo}
          examName={sessionInfo.courseName}
          uploadDuration={uploadDuration}
          onExpire={() => setIsExpired(true)}
        />
      </Box>

      {/* ── Scrollable questions area ─────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0, 
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(63,65,209,0.25)",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "rgba(63,65,209,0.45)",
          },
        }}
      >
        <Container maxWidth="lg" sx={{ py: 3, px: { xs: 2, sm: 3 }, pb: "100px" }}>

          {/* Timer expired banner */}
          {isExpired && (
            <Box
              sx={{
                mb: 2.5,
                p: 2,
                backgroundColor: "#fce4ec",
                borderRadius: "8px",
                textAlign: "center",
                border: "1px solid #f8bbd0",
              }}
            >
              <Typography
                sx={{ color: "#c62828", fontWeight: "bold", fontSize: "14px" }}
              >
                Answer Upload Time is Over
              </Typography>
            </Box>
          )}

          {/* Submission error banner */}
          {submitError && (
            <Box
              sx={{
                mb: 2.5,
                p: 2,
                backgroundColor: "#fce4ec",
                borderRadius: "8px",
                textAlign: "center",
                border: "1px solid #f8bbd0",
              }}
            >
              <Typography sx={{ color: "#c62828", fontSize: "14px" }}>
                {submitError}
              </Typography>
            </Box>
          )}

          {/* Question cards */}
          {questions.map((question) => (
            <QuestionCard
              key={question.id}
              question={question}
              uploadState={uploadStates[question.id]}
              onFileSelect={handleFileSelect}
              onDelete={handleDelete}
              onView={handleView}
              onRetry={handleRetry}
              disabled={uploadLocked}
            />
          ))}

          <Box sx={{ height: { xs: "16px", sm: "8px" } }} />
        </Container>
      </Box>

      {/* ── Fixed Submit Bar ──────────────────────────────────────────────── */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          zIndex: 1000,
          backgroundColor: "#FFFFFF",
          py: { xs: 2, sm: 2 },
          px: { xs: 2, sm: 3 },
          display: "flex",
          justifyContent: "center",
          boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <Button
          onClick={handleSubmitClick}
          disabled={submitDisabled}
          sx={{
            backgroundColor: submitDisabled ? "#d0d0d0" : "#E598AC",
            color: submitDisabled ? "#999" : "#000000",
            borderRadius: "28px",
            px: { xs: 6, sm: 10 },
            py: { xs: 1.2, sm: 1 },
            fontSize: { xs: "13px", sm: "14px" },
            fontWeight: "bold",
            letterSpacing: "1.5px",
            textTransform: "uppercase",
            minWidth: { xs: "160px", sm: "220px" },
            transition: "all 0.2s",
            "&:hover": {
              backgroundColor: submitDisabled ? "#d0d0d0" : "#d4849a",
              boxShadow: submitDisabled
                ? "none"
                : "0 6px 16px rgba(229,152,172,0.5)",
            },
          }}
        >
          Submit
        </Button>
      </Box>

      {/* ── Confirmation Modal ────────────────────────────────────────────── */}
      <ConfirmSubmitModal
        open={showConfirmModal}
        onConfirm={handleConfirmSubmit}
        onCancel={handleCancelSubmit}
        isSubmitting={isSubmitting}
      />

      {/* ── Success Modal ─────────────────────────────────────────────────── */}
      <SuccessModal open={showSuccessModal} />
    </Box>
  );
}